import { memo, useCallback } from 'react';

import { Check, Copy, Eye } from '@air/next-icons';
import { Button } from '@air/primitive-button';

import { useCopyTextToClipboard } from '~/hooks/useCopyTextToClipboard';

export type ShareLinkActionConfirmationToastContentProps = {
  message: string;
  shareUrl: string;
  onDismiss: () => void;
};

export const ShareLinkActionConfirmationToastContent = memo(
  ({ message, shareUrl, onDismiss }: ShareLinkActionConfirmationToastContentProps) => {
    const { copyTextToClipboard } = useCopyTextToClipboard();

    const onClickHandlePreviewLink = useCallback(() => {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
      onDismiss();
    }, [shareUrl, onDismiss]);

    const onClickHandleCopyLink = useCallback(() => {
      copyTextToClipboard({ text: shareUrl, successMessage: 'Link copied' });
      onDismiss();
    }, [shareUrl, onDismiss, copyTextToClipboard]);

    return (
      <div className="flex items-center justify-between gap-4 overflow-hidden rounded-12 bg-grey-1 p-4 shadow-popover">
        <div className="flex items-center gap-4">
          <div className="shrink-0 rounded-full bg-blue-2 p-2">
            <Check className="size-6 text-blue-9" />
          </div>
          <strong className="text-14 text-grey-12">{message}</strong>
        </div>
        <div className="flex items-center gap-2">
          <Button
            appearance="outline"
            color="grey"
            size="small"
            onClick={onClickHandleCopyLink}
            prefix={<Copy className="size-3" />}
            type="button"
            className="shrink-0"
          >
            Copy
          </Button>
          <Button
            appearance="outline"
            color="grey"
            size="small"
            onClick={onClickHandlePreviewLink}
            prefix={<Eye className="size-3" />}
            type="button"
            className="shrink-0"
          >
            Preview
          </Button>
        </div>
      </div>
    );
  },
);

ShareLinkActionConfirmationToastContent.displayName = 'ShareLinkActionConfirmationToastContent';
